import React from 'react';

/**
 * Drop-in replacement for `useState` that persists the value to
 * `localStorage` under the given key.
 *
 * - Reads the previous value on mount (falls back to `initial` if missing,
 *   corrupt, or in private/quota-blocked browsers).
 * - Writes on every change. Stringifies via JSON, so `initial` should be
 *   serializable (string / number / boolean / plain object / array).
 *
 * Usage:
 *   const [statusFilter, setStatusFilter] = usePersistedState(
 *     'invoices.statusFilter',
 *     'all'
 *   );
 */
export const usePersistedState = (storageKey, initial) => {
  const read = () => {
    if (!storageKey) return initial;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw == null) return initial;
      return JSON.parse(raw);
    } catch (_) {
      return initial;
    }
  };

  const [value, setValue] = React.useState(read);

  React.useEffect(() => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(value));
    } catch (_) { /* quota / private mode — silently ignore */ }
  }, [storageKey, value]);

  return [value, setValue];
};
